import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Link as LinkIcon, Copy, RefreshCcw, BedDouble, Bath, Ruler } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Property prices are stored in SGD and shown to clients in CHF.
import { useSgdChfRate, sgdToChf } from "@/lib/currency";

export default function ShortlistManager({ open, onClose, lead }) {
  const chfRate = useSgdChfRate();
  const fmt = (n) => sgdToChf(n || 0, chfRate);
  const [allProps, setAllProps] = useState([]);
  const [selected, setSelected] = useState([]);
  const [shareToken, setShareToken] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !lead) return;
    (async () => {
      const [propsRes, listRes] = await Promise.all([
        api.get("/properties"),
        api.get(`/leads/${lead.lead_id}/shortlist`),
      ]);
      setAllProps(propsRes.data);
      setSelected(listRes.data.property_ids || []);
      setShareToken(listRes.data.share_token || null);
    })();
  }, [open, lead]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/leads/${lead.lead_id}/shortlist`, { property_ids: selected });
      toast.success("Shortlist saved");
    } catch (_e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const genLink = async () => {
    try {
      const res = await api.post(`/leads/${lead.lead_id}/share`);
      setShareToken(res.data.share_token);
      toast.success("Share link generated");
    } catch (_e) { toast.error("Failed"); }
  };

  const shareUrl = shareToken ? `${window.location.origin}/shortlist/${shareToken}` : "";

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied to clipboard");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="shortlist-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Shortlist for {lead?.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-[#F9FAFB] border border-[#E2E8F0] rounded-md p-4">
            <div className="label-eyebrow mb-2 flex items-center gap-2"><LinkIcon className="w-3 h-3" /> Shareable link (no login required)</div>
            {shareToken ? (
              <div className="flex items-center gap-2">
                <Input data-testid="share-url" readOnly value={shareUrl} className="text-xs font-mono" />
                <Button data-testid="copy-share-btn" size="icon" variant="ghost" onClick={copyLink}><Copy className="w-4 h-4" /></Button>
                <Button data-testid="rotate-share-btn" size="icon" variant="ghost" onClick={genLink} title="Rotate"><RefreshCcw className="w-4 h-4" /></Button>
              </div>
            ) : (
              <Button data-testid="gen-share-btn" onClick={genLink} className="bg-[#0A1128] text-white hover:bg-[#152243]" size="sm">
                <LinkIcon className="w-4 h-4 mr-2" /> Generate private link
              </Button>
            )}
          </div>

          <div>
            <div className="label-eyebrow mb-3">Select properties ({selected.length} chosen)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[45vh] overflow-y-auto pr-1">
              {allProps.length === 0 && <div className="col-span-full text-sm text-slate-500">No properties yet. Add listings first.</div>}
              {allProps.map((p) => {
                const on = selected.includes(p.property_id);
                return (
                  <div
                    key={p.property_id}
                    data-testid={`shortlist-item-${p.property_id}`}
                    onClick={() => toggle(p.property_id)}
                    className={`flex gap-3 border rounded-md p-3 cursor-pointer transition-colors ${on ? "border-[#C5A880] bg-[#FDF8EF]/50" : "border-[#E2E8F0] hover:border-[#CBD5E1]"}`}
                  >
                    <Checkbox checked={on} onCheckedChange={() => toggle(p.property_id)} onClick={(e) => e.stopPropagation()} className="mt-1" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#0A1128] truncate">{p.title}</div>
                      <div className="text-xs text-slate-500 truncate">{p.district}</div>
                      <div className="flex items-center gap-3 text-[11px] text-slate-600 mt-1">
                        <span className="flex items-center gap-1"><BedDouble className="w-3 h-3" /> {p.bedrooms}</span>
                        <span className="flex items-center gap-1"><Bath className="w-3 h-3" /> {p.bathrooms}</span>
                        <span className="flex items-center gap-1"><Ruler className="w-3 h-3" /> {p.sqft}</span>
                        <span className="ml-auto font-medium text-[#0A1128]">{fmt(p.price_sgd)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button data-testid="shortlist-save" onClick={save} disabled={saving} className="bg-[#0A1128] text-white hover:bg-[#152243]">
            {saving ? "Saving…" : "Save shortlist"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
